export interface IndustryInfo {
  value: string;
  label: string;
  description: string;
}

export interface IndustryGroup {
  name: string;
  industries: IndustryInfo[];
}

export const INDUSTRY_GROUPS: IndustryGroup[] = [
  {
    name: "General",
    industries: [
      {
        value: "general",
        label: "General Business",
        description: "Standard chart of accounts for any business type",
      },
      {
        value: "personal",
        label: "Personal Finance",
        description: "Personal income, expenses, and investment tracking",
      },
    ],
  },
  {
    name: "Healthcare",
    industries: [
      {
        value: "dentist",
        label: "Dental / Medical Practice",
        description: "Patient revenue, insurance receivables, medical supplies, lab fees",
      },
      {
        value: "veterinarian",
        label: "Veterinary Practice",
        description: "Patient revenue, medical inventory, lab work, boarding income",
      },
      {
        value: "chiropractor",
        label: "Chiropractic Practice",
        description: "Patient visits, insurance billing, therapy supplies",
      },
      {
        value: "physical-therapy",
        label: "Physical Therapy",
        description: "Therapy sessions, insurance claims, rehab equipment",
      },
      {
        value: "pharmacy",
        label: "Pharmacy",
        description: "Prescription revenue, inventory management, insurance processing",
      },
      {
        value: "home-health",
        label: "Home Health Care",
        description: "Caregiver wages, patient billing, mileage, medical supplies",
      },
    ],
  },
  {
    name: "Professional Services",
    industries: [
      {
        value: "attorney",
        label: "Law Practice",
        description: "Trust accounts, billable hours, retainer tracking, court fees",
      },
      {
        value: "accounting-firm",
        label: "Accounting / CPA Firm",
        description: "Client billing, professional development, tax software",
      },
      {
        value: "architect",
        label: "Architecture Firm",
        description: "Project billing, design software, professional liability",
      },
      {
        value: "engineering-firm",
        label: "Engineering Firm",
        description: "Project-based revenue, testing equipment, professional certifications",
      },
      {
        value: "consulting",
        label: "Consulting",
        description: "Consulting fees, project expenses, travel, subcontractors",
      },
      {
        value: "real-estate-agent",
        label: "Real Estate Agent / Broker",
        description: "Commissions, MLS fees, marketing, staging costs",
      },
      {
        value: "insurance-agency",
        label: "Insurance Agency",
        description: "Commission income, policy management, licensing fees",
      },
    ],
  },
  {
    name: "Trades & Construction",
    industries: [
      {
        value: "contractor",
        label: "Contractor / Construction",
        description: "Job costing, retainage tracking, subcontractor management, materials",
      },
      {
        value: "electrician",
        label: "Electrician",
        description: "Service calls, materials, permits, vehicle expenses",
      },
      {
        value: "plumber",
        label: "Plumber",
        description: "Service revenue, parts inventory, equipment, licensing",
      },
      {
        value: "hvac",
        label: "HVAC",
        description: "Service contracts, equipment sales, refrigerant inventory",
      },
      {
        value: "roofing",
        label: "Roofing Contractor",
        description: "Job estimates, material costs, subcontractors, insurance",
      },
      {
        value: "landscaping",
        label: "Landscaping / Lawn Care",
        description: "Service contracts, equipment maintenance, seasonal revenue",
      },
      {
        value: "auto-mechanic",
        label: "Auto Repair / Mechanic",
        description: "Labor revenue, parts inventory, diagnostic equipment",
      },
    ],
  },
  {
    name: "Food & Beverage",
    industries: [
      {
        value: "restaurant",
        label: "Restaurant",
        description: "Food cost tracking, tip management, point-of-sale, inventory",
      },
      {
        value: "bakery",
        label: "Bakery",
        description: "Ingredient costs, wholesale and retail revenue, equipment",
      },
      {
        value: "food-truck",
        label: "Food Truck",
        description: "Mobile food sales, commissary fees, permits, vehicle costs",
      },
      {
        value: "catering",
        label: "Catering",
        description: "Event billing, food costs, staffing, equipment rental",
      },
      {
        value: "bar-brewery",
        label: "Bar / Brewery",
        description: "Beverage costs, licensing, entertainment, excise taxes",
      },
      {
        value: "coffee-shop",
        label: "Coffee Shop",
        description: "Beverage and food revenue, inventory, equipment maintenance",
      },
    ],
  },
  {
    name: "Retail & E-Commerce",
    industries: [
      {
        value: "retail-store",
        label: "Retail Store",
        description: "Sales revenue, inventory management, point-of-sale, shrinkage",
      },
      {
        value: "ecommerce",
        label: "E-Commerce",
        description: "Online sales, shipping costs, platform fees, returns",
      },
      {
        value: "auto-dealership",
        label: "Auto Dealership",
        description: "Vehicle sales, floor plan financing, service department, parts",
      },
    ],
  },
  {
    name: "Services & Personal Care",
    industries: [
      {
        value: "salon-barbershop",
        label: "Salon / Barbershop",
        description: "Service revenue, booth rental, product sales, supplies",
      },
      {
        value: "spa",
        label: "Spa / Massage Therapy",
        description: "Treatment revenue, product sales, practitioner pay, supplies",
      },
      {
        value: "gym-fitness",
        label: "Gym / Fitness Studio",
        description: "Membership revenue, class fees, equipment, trainer pay",
      },
      {
        value: "daycare",
        label: "Daycare / Child Care",
        description: "Tuition, meal costs, supplies, licensing, staff wages",
      },
      {
        value: "cleaning-service",
        label: "Cleaning Service",
        description: "Service contracts, supplies, equipment, vehicle costs",
      },
    ],
  },
  {
    name: "Creative & Technology",
    industries: [
      {
        value: "photography",
        label: "Photography",
        description: "Session fees, prints and products, equipment, editing software",
      },
      {
        value: "graphic-design",
        label: "Graphic Design",
        description: "Project billing, software subscriptions, stock assets, equipment",
      },
      {
        value: "web-development",
        label: "Web Development / Software",
        description: "Project and retainer billing, hosting, software tools, subcontractors",
      },
      {
        value: "marketing-agency",
        label: "Marketing Agency",
        description: "Campaign billing, ad spend passthrough, media buying, creative costs",
      },
    ],
  },
  {
    name: "Transportation & Logistics",
    industries: [
      {
        value: "trucking",
        label: "Trucking / Freight",
        description: "Freight revenue, fuel costs, maintenance, IFTA reporting",
      },
      {
        value: "taxi-rideshare",
        label: "Taxi / Rideshare",
        description: "Ride revenue, vehicle expenses, platform fees, mileage",
      },
      {
        value: "moving-company",
        label: "Moving Company",
        description: "Job billing, vehicle costs, labor, packing supplies, insurance",
      },
    ],
  },
  {
    name: "Real Estate & Property",
    industries: [
      {
        value: "property-management",
        label: "Property Management",
        description: "Rental income, maintenance costs, tenant deposits, management fees",
      },
      {
        value: "real-estate-investor",
        label: "Real Estate Investor",
        description: "Rental income, mortgage tracking, depreciation, capital improvements",
      },
      {
        value: "hotel-motel",
        label: "Hotel / Motel / B&B",
        description: "Room revenue, occupancy tracking, housekeeping, amenities",
      },
    ],
  },
  {
    name: "Education & Agriculture",
    industries: [
      {
        value: "tutoring",
        label: "Tutoring / Education",
        description: "Session billing, curriculum materials, platform fees",
      },
      {
        value: "farming",
        label: "Farming / Agriculture",
        description: "Crop revenue, seed and feed, equipment, seasonal labor",
      },
      {
        value: "ranching",
        label: "Ranching / Livestock",
        description: "Livestock sales, feed costs, veterinary, equipment, land",
      },
    ],
  },
  {
    name: "Nonprofit",
    industries: [
      {
        value: "nonprofit",
        label: "Nonprofit Organization",
        description: "Donations, grants, program expenses, fundraising costs, fund accounting",
      },
    ],
  },
];

/** Flat lookup: industry value to IndustryInfo */
export const INDUSTRY_INFO_MAP: Map<string, IndustryInfo> = new Map(
  INDUSTRY_GROUPS.flatMap((g) => g.industries.map((i) => [i.value, i])),
);

/** The personal chart's industry: offered only through the personal-books choice (5d). */
export const PERSONAL_INDUSTRY = "personal";

/**
 * The industry picker for a business: every group without the personal
 * chart, so a business is never set up on it (requirements 5d, Advocate M4).
 */
export const BUSINESS_INDUSTRY_GROUPS: IndustryGroup[] = INDUSTRY_GROUPS.map((group) => ({
  ...group,
  industries: group.industries.filter((i) => i.value !== PERSONAL_INDUSTRY),
})).filter((group) => group.industries.length > 0);
